// frontend/admin-portal/src/pages/Dashboard.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Button, Spinner, formatMoney } from "@fiberhome/shared-ui";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { useDashboardData } from "../hooks/useDashboardData";

import StatCard from "../components/dashboard/StatCard";
import ProductsOverview from "../components/dashboard/ProductsOverview";
import LowStockList from "../components/dashboard/LowStockList";
import TreasurySnapshot from "../components/dashboard/TreasurySnapshot";
import OrdersSnapshot from "../components/dashboard/OrdersSnapshot";
import RecentOrdersTable from "../components/dashboard/RecentOrdersTable";
import ProcurementSnapshot from "../components/dashboard/ProcurementSnapshot";
import ProfitBreakdownTabs from "../components/treasury/ProfitBreakdownTabs";

import "../styles/dashboard.css";

export default function Dashboard() {
  const { user } = useAuth();
  const { get } = useSettings();
  const navigate = useNavigate();
  const d = useDashboardData();

  const currency = get("finance.baseCurrency", "USD");
  const firstName = user?.fullName?.split(" ")[0] || "Admin";

  const isAnythingLoading =
    d.productStats.loading || d.orderStats.loading || d.treasury.loading;

  const treasuryTotal = (d.treasury.data?.accounts || []).reduce(
    (s, a) => s + (Number(a.currentBalance) || 0),
    0,
  );

  return (
    <>
      <PageHeader
        title={`Welcome back, ${firstName}`}
        subtitle="FiberHome store & showroom overview"
        breadcrumbs={["Home", "Dashboard"]}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={d.refresh}
            disabled={isAnythingLoading}
          >
            {isAnythingLoading ? <Spinner size="sm" /> : "Refresh"}
          </Button>
        }
      />

      {/* ── KPI row ─────────────────────────────────────────── */}
      <div className="fh-dash-kpis">
        <StatCard
          label="Net Profit (MTD)"
          value={
            d.treasury.data?.profit
              ? formatMoney(d.treasury.data.profit.netProfit, {
                  symbol: d.treasury.data.profit.currency,
                })
              : undefined
          }
          subValue={
            d.treasury.data?.profit
              ? `Revenue ${formatMoney(d.treasury.data.profit.revenue, {
                  symbol: d.treasury.data.profit.currency,
                })}`
              : undefined
          }
          loading={d.treasury.loading}
          error={d.treasury.error}
          onClick={() => navigate("/treasury")}
          icon="📈"
          tone="purple"
        />

        <StatCard
          label="Expected Profit"
          value={
            d.treasury.data?.expectedProfit
              ? formatMoney(d.treasury.data.expectedProfit.expectedProfit, {
                  symbol: d.treasury.data.expectedProfit.currency,
                })
              : undefined
          }
          subValue={
            d.treasury.data?.expectedProfit
              ? `${d.treasury.data.expectedProfit.marginPct}% margin · ${d.treasury.data.expectedProfit.totalUnits} units`
              : undefined
          }
          loading={d.treasury.loading}
          error={d.treasury.error}
          onClick={() => navigate("/treasury")}
          icon="🎯"
          tone="info"
        />

        <StatCard
          label="Products"
          value={d.productStats.data?.total}
          subValue={
            d.productStats.data
              ? `${d.productStats.data.active} active`
              : undefined
          }
          loading={d.productStats.loading}
          error={d.productStats.error}
          onClick={() => navigate("/products")}
          icon="📦"
          tone="primary"
        />
        <StatCard
          label="Orders Today"
          value={d.orderStats.data?.today ?? d.orderStats.data?.total}
          subValue={
            d.orderStats.data?.pending != null
              ? `${d.orderStats.data.pending} pending`
              : undefined
          }
          loading={d.orderStats.loading}
          error={d.orderStats.error}
          onClick={() => navigate("/orders")}
          icon="🧾"
          tone="info"
        />
        <StatCard
          label="Low Stock"
          value={d.productStats.data?.lowStock}
          subValue={
            d.productStats.data?.outOfStock != null
              ? `${d.productStats.data.outOfStock} out of stock`
              : undefined
          }
          loading={d.productStats.loading}
          error={d.productStats.error}
          onClick={() => navigate("/products?lowStock=true")}
          icon="⚠️"
          tone="warning"
        />
        <StatCard
          label="Treasury Balance"
          value={
            d.treasury.data
              ? formatMoney(treasuryTotal, {
                  symbol: d.treasury.data.baseCurrency || currency,
                })
              : undefined
          }
          subValue={`${(d.treasury.data?.accounts || []).length} accounts`}
          loading={d.treasury.loading}
          error={d.treasury.error}
          onClick={() => navigate("/treasury")}
          icon="💰"
          tone="success"
        />
      </div>

      {/* ── Row 1 ──────────────────────────────────────────── */}
      <div className="fh-dash-row">
        <ProductsOverview
          slice={d.productStats}
          onViewAll={() => navigate("/products")}
        />
        <TreasurySnapshot slice={d.treasury} />
      </div>

      {/* ── Row 2 ──────────────────────────────────────────── */}
      <div className="fh-dash-row">
        <OrdersSnapshot
          slice={d.orderStats}
          onViewAll={() => navigate("/orders")}
        />
        <LowStockList slice={d.lowStock} />
      </div>

      {/* ── Row 3 ──────────────────────────────────────────── */}
      <div className="fh-dash-row fh-dash-row--3-2">
        <RecentOrdersTable
          slice={d.recentOrders}
          onViewAll={() => navigate("/orders")}
        />
        <ProcurementSnapshot
          poSlice={d.poStats}
          rfqSlice={d.pendingRfqs}
          receiptSlice={d.goodsReceipts}
        />
      </div>
      <div className="fh-dash-row">
        <div className="fh-dash-grid fh-dash-grid--1">
          <div className="fh-kpi-panel">
            <div className="fh-kpi-panel__head">
              <h3 className="fh-kpi-panel__title">Profit by Category</h3>
            </div>
            <div className="fh-kpi-panel__body">
              <ProfitBreakdownTabs
                breakdown={d.treasury.data?.profit?.breakdown}
                currency={d.treasury.data?.profit?.currency || currency}
                emptyMessage="No sales in this period"
              />
            </div>
          </div>

          <div className="fh-kpi-panel">
            <div className="fh-kpi-panel__head">
              <h3 className="fh-kpi-panel__title">
                Expected Profit by Category
              </h3>
            </div>
            <div className="fh-kpi-panel__body">
              <ProfitBreakdownTabs
                breakdown={d.treasury.data?.expectedProfit?.breakdown}
                currency={d.treasury.data?.expectedProfit?.currency || currency}
                emptyMessage="No inventory in stock"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
